import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { SquareIcon, Calculator, Info } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './RoomAreaCalculatorPage/components/CalculationForm';
import ResultsOverview from './RoomAreaCalculatorPage/components/ResultsOverview';
import RoomVisualization from './RoomAreaCalculatorPage/components/RoomVisualization';
import RoomShapesGallery from './RoomAreaCalculatorPage/components/RoomShapesGallery';
import MaterialCalculator from './RoomAreaCalculatorPage/components/MaterialCalculator';
import QuickCalculationSidebar from './RoomAreaCalculatorPage/components/QuickCalculationSidebar';
import ComparisonSidebar from './RoomAreaCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './RoomAreaCalculatorPage/components/TipsSidebar';
import InfoContent from './RoomAreaCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  roomShape: 'rectangle',
  measurementUnit: 'meters',
  // Прямоугольная комната
  length: '5.0',
  width: '4.0',
  // Комната L-формы
  mainLength: '6.0',
  mainWidth: '4.0',
  extensionLength: '3.0',
  extensionWidth: '2.0',
  // Комната со скошенным углом
  baseLength: '5.0',
  baseWidth: '4.0',
  cutoutLength: '1.0', 
  cutoutWidth: '1.0',
  // Комната с эркером
  roomLength: '5.0',
  roomWidth: '4.0',
  bayWindowDepth: '1.5',
  bayWindowWidth: '3.0',
  // Комната с нишей
  roomLengthWithNiche: '5.0',
  roomWidthWithNiche: '4.0',
  nicheDepth: '0.6',
  nicheWidth: '2.0',
  // Комната с колонной
  roomLengthWithColumn: '5.0',
  roomWidthWithColumn: '4.0',
  columnSide: '0.4',
  // Комната произвольной формы (многоугольник)
  polygonPoints: [
    { x: 0, y: 0 },
    { x: 5, y: 0 },
    { x: 5, y: 4 },
    { x: 0, y: 4 },
  ],
  // Круглая комната
  diameter: '4.0',
  // Высота потолков
  ceilingHeight: '2.7',
  // Дополнительные параметры
  includeSkirting: true,
  skirtingHeight: '0.1',
  includeWindows: true,
  windowCount: '2',
  windowHeight: '1.5',
  windowWidth: '1.2',
  includeDoors: true,
  doorCount: '1',
  doorHeight: '2.0',
  doorWidth: '0.8',
  includeBalconies: false,
  balconyLength: '3.0',
  balconyWidth: '1.2',
  // Материалы для отделки
  flooringType: 'laminate',
  flooringUnitCost: '800',
  wallMaterialType: 'paint',
  wallMaterialUnitCost: '250',
  ceilingType: 'paint',
  ceilingUnitCost: '200'
};

// Типы форм комнаты
export const roomShapes = [
  { id: 'rectangle', name: 'Прямоугольная комната', icon: 'Square' },
  { id: 'l-shaped', name: 'Г-образная комната', icon: 'CornerRightDown' },
  { id: 'angled', name: 'Комната со скошенным углом', icon: 'CornerDownRight' },
  { id: 'bay-window', name: 'Комната с эркером', icon: 'LayoutTemplate' },
  { id: 'niche', name: 'Комната с нишей', icon: 'LayoutList' },
  { id: 'column', name: 'Комната с колонной', icon: 'Pilcrow' },
  { id: 'polygon', name: 'Произвольная форма', icon: 'Pen' },
  { id: 'circle', name: 'Круглая комната', icon: 'Circle' }
];

// Единицы измерения
export const measurementUnits = [
  { id: 'meters', name: 'Метры', acronym: 'м' },
  { id: 'centimeters', name: 'Сантиметры', acronym: 'см' },
  { id: 'millimeters', name: 'Миллиметры', acronym: 'мм' }
];

// Типы напольных покрытий
export const flooringTypes = [
  { id: 'laminate', name: 'Ламинат', standardPrice: 800 },
  { id: 'parquet', name: 'Паркет', standardPrice: 2500 },
  { id: 'vinyl', name: 'Виниловое покрытие', standardPrice: 950 },
  { id: 'tile', name: 'Плитка', standardPrice: 1200 },
  { id: 'carpet', name: 'Ковролин', standardPrice: 600 },
  { id: 'linoleum', name: 'Линолеум', standardPrice: 450 },
  { id: 'concrete', name: 'Бетонная стяжка', standardPrice: 350 },
  { id: 'self-leveling', name: 'Наливной пол', standardPrice: 1000 }
];

// Типы отделки стен
export const wallMaterialTypes = [
  { id: 'paint', name: 'Краска', standardPrice: 250, unit: 'м²' },
  { id: 'wallpaper', name: 'Обои', standardPrice: 450, unit: 'м²' },
  { id: 'plaster', name: 'Декоративная штукатурка', standardPrice: 850, unit: 'м²' },
  { id: 'tile', name: 'Плитка настенная', standardPrice: 1150, unit: 'м²' },
  { id: 'panels', name: 'Стеновые панели', standardPrice: 1450, unit: 'м²' },
  { id: 'brick', name: 'Декоративный кирпич', standardPrice: 1750, unit: 'м²' },
  { id: 'wood', name: 'Деревянная отделка', standardPrice: 2200, unit: 'м²' }
];

// Типы отделки потолка
export const ceilingTypes = [
  { id: 'paint', name: 'Покраска', standardPrice: 200, unit: 'м²' },
  { id: 'stretch', name: 'Натяжной потолок', standardPrice: 650, unit: 'м²' },
  { id: 'drywall', name: 'Гипсокартон', standardPrice: 950, unit: 'м²' },
  { id: 'panels', name: 'Потолочные панели', standardPrice: 1200, unit: 'м²' },
  { id: 'decorative', name: 'Декоративная отделка', standardPrice: 1600, unit: 'м²' }
];

/**
 * RoomAreaCalculatorPage Component - Калькулятор площади помещения с различными формами и дополнительными параметрами.
 */
const RoomAreaCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('room-area-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор площади помещения | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет площади помещения различной формы: прямоугольные, Г-образные, с нишами, эркерами, колоннами и другими особенностями планировки. Расчет площади пола, стен и потолка для ремонта и отделки.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedRooms, setSavedRooms] = useState([]);
  const [showMaterialCalculator, setShowMaterialCalculator] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных комнат из localStorage ---
  useEffect(() => {
    const savedRoomsData = localStorage.getItem('savedRooms');
    if (savedRoomsData) {
      try {
        setSavedRooms(JSON.parse(savedRoomsData));
      } catch (e) {
        console.error("Error loading saved rooms:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'rectangle':
          newState.roomShape = 'rectangle';
          break;
        case 'l-shaped':
          newState.roomShape = 'l-shaped';
          break;
        case 'angled':
          newState.roomShape = 'angled';
          break;
        case 'bay-window':
          newState.roomShape = 'bay-window';
          break;
        case 'niche':
          newState.roomShape = 'niche';
          break;
        case 'polygon':
          newState.roomShape = 'polygon';
          break;
        case 'materials':
          newState = { ...prev };
          setShowMaterialCalculator(true);
          break;
        case 'full-apartment':
          newState = { ...prev };
          // Здесь логика для полной квартиры
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение комнаты в localStorage
  const saveRoom = (name) => {
    if (!results) return;

    const newSavedRoom = {
      id: Date.now().toString(),
      name: name || `Комната ${results.floorArea.toFixed(2)}м²`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      roomShape: formData.roomShape
    };

    const updatedSavedRooms = [...savedRooms, newSavedRoom];
    setSavedRooms(updatedSavedRooms);
    localStorage.setItem('savedRooms', JSON.stringify(updatedSavedRooms));

    return newSavedRoom;
  };

  // Удаление сохраненной комнаты
  const deleteSavedRoom = (id) => {
    const updatedSavedRooms = savedRooms.filter(room => room.id !== id);
    setSavedRooms(updatedSavedRooms);
    localStorage.setItem('savedRooms', JSON.stringify(updatedSavedRooms));
  };

  // Загрузка сохраненной комнаты
  const loadSavedRoom = (id) => {
    const roomToLoad = savedRooms.find(room => room.id === id);
    if (roomToLoad) {
      setFormData(roomToLoad.formData);
      setResults(roomToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <SquareIcon className="w-8 h-8 text-fuchsia-600 dark:text-fuchsia-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор площади помещения'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              roomShapes={roomShapes}
              measurementUnits={measurementUnits}
              flooringTypes={flooringTypes}
              wallMaterialTypes={wallMaterialTypes}
              ceilingTypes={ceilingTypes}
              saveRoom={saveRoom}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview 
                  results={results} 
                  formData={formData}
                  setShowMaterialCalculator={setShowMaterialCalculator}
                />
                <RoomVisualization 
                  results={results}
                  formData={formData}
                />
              </>
            )}

            {/* --- Калькулятор материалов --- */}
            {showMaterialCalculator && results && (
              <MaterialCalculator 
                results={results}
                formData={formData}
                flooringTypes={flooringTypes}
                wallMaterialTypes={wallMaterialTypes}
                ceilingTypes={ceilingTypes}
              />
            )}

            {/* --- Галерея форм помещений --- */}
            <RoomShapesGallery 
              roomShapes={roomShapes}
              setFormData={setFormData}
              currentShape={formData.roomShape}
            />

            {/* --- Информационный контент о расчетах площади --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрого расчета --- */}
            <QuickCalculationSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              setResults={setResults}
            />

            {/* --- Панель сравнения комнат --- */}
            <ComparisonSidebar 
              savedRooms={savedRooms}
              deleteSavedRoom={deleteSavedRoom}
              loadSavedRoom={loadSavedRoom}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-fuchsia-500 focus:ring-fuchsia-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-fuchsia-600 focus:ring-fuchsia-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-fuchsia-600 focus:ring-fuchsia-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Room Visualization Styles */
        .room-visualization {
          @apply border border-dashed border-gray-400 dark:border-gray-500;
        }
        .room-wall {
          @apply bg-gray-800 dark:bg-gray-200;
        }
        .room-window {
          @apply bg-blue-200 dark:bg-blue-800;
        }
        .room-door {
          @apply bg-amber-600 dark:bg-amber-800;
        }
        .room-column {
          @apply bg-gray-400 dark:bg-gray-600;
        }
      `}</style>
    </div>
  );
};

export default RoomAreaCalculatorPage;